import { useLogger } from '../../composables/useLogger'
import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { Image } from './Image'
import { getUserId } from '../utils/userUtils';
import type { ImageBlob } from './ImageBlob';

const logger = useLogger()

export class AlbumImage {
    id: string;
    album_id: string | null;
    image_id: string | null;
    sort_order: number | null;
    user_id:string | null;
    caption: string;
    image?: Image;

    constructor() {
        this.id = null;
        this.album_id = null;
        this.image_id = null;
        this.sort_order = null;
        this.user_id = null;
        this.caption = "";
    }

    // static method to get the supabase client
    static getSupabase() {
        const supabaseClientStore = useSupabaseClientStore()
        return supabaseClientStore.supabase
    }

    static async fromDatabaseRow(row: {
        id: string;
        album_id: string;
        image_id: string;
        sort_order: number;
        user_id:string;
        caption: string;
    }): Promise<AlbumImage> {
        const albumImage = new AlbumImage();
        albumImage.id = row.id;
        albumImage.album_id = row.album_id;
        albumImage.image_id = row.image_id;
        albumImage.sort_order = row.sort_order;
        albumImage.user_id = row.user_id;
        albumImage.caption = row.caption;
        albumImage.image = await Image.fromDatabase(albumImage.image_id);
        return albumImage;
    }


    /**
     * Get all images in albumimages in any album for a user without album information - for use in user deletion.
     * @returns {Promise<AlbumImage[]>} The album images.
     */
    static async getAllAlbumImagesForUser(userId: string): Promise<AlbumImage[]> {
        const { data, error } = await AlbumImage.getSupabase()
            .from('albumimages')
            .select('*')
            .eq('user_id', userId)

        if (error) logger.throwSupabaseError(error, `Failed to get all album images for user ${userId}`)
        const albumImages = await Promise.all(data.map(row => AlbumImage.fromDatabaseRow(row)))
        return albumImages
    }

    /**
     * Delete all albumimages for a user - for use in user deletion.
     * Needed since Images and therefore also ImageBlobs are not deleted in a cascade from albumimages.
     * @param {string} userId - The id of the user to delete the albumimages for.
     * @returns {Promise<void>}
     */
    static async deleteAllAlbumImagesForUser(userId: string): Promise<void> {
        try {
            const albumImages = await AlbumImage.getAllAlbumImagesForUser(userId)
            for (const albumImage of albumImages) {
                const imageToDelete = albumImage.image

                // delete image and blob
                if (imageToDelete) {
                    await Image.deleteImage(imageToDelete.id)
                }

                // directly delete albumimage from database
                const { error } = await AlbumImage.getSupabase()
                    .from('albumimages')
                    .delete()
                    .eq('id', albumImage.id)
                if (error) logger.throwSupabaseError(error, `Failed to delete album image ${albumImage.id}`)
            }
        } catch (error) {
            logger.logErrorCaught("Error in deleteAllAlbumImagesForUser", "AlbumImage.ts", error)
            logger.throwSupabaseError(error, `Failed to delete all album images for user ${userId}`)
        }
    }

    async updateCaption(newCaption: string): Promise<void> {
        const { error: updateError } = await AlbumImage.getSupabase()
            .from('albumimages')    
            .update({ caption: newCaption })
            .eq('id', this.id)
        if (updateError) {
            logger.throwSupabaseError(updateError, `Failed to update caption for album image ${this.id}`)
        }
    }

    static async batchUpdateAlbumImageOrder(updatedAlbumImages: { 
        id: string, 
        album_id: string, 
        image_id: string, 
        sort_order: number 
    }[]) {
        logger.debug('SortImages: batchUpdateAlbumImageOrder', { updatedAlbumImages: updatedAlbumImages })

        const { error } = await AlbumImage.getSupabase()
            .from('albumimages')
            .upsert(updatedAlbumImages, { onConflict: 'id' }) 

        if (error) {
            logger.error('Error upserting album image order:', error)
            logger.throwSupabaseError(error, 'Failed to update album image order')
        }
    }


}
